<template>
  <div v-if="prototype" class="z-10 h-full overflow-x-hidden">
    <BaseTable
      :headers="prototypeDataHeaders"
      :list="prototype.qstQuestionnairePrototypeData"
      is-add-new-hidden
      table-id="prototype-data-table"
      only-search
      :group-by-headers="
        prototype.qstQuestionnairePrototypeData[0].prototypeDataMaterialGroup?.materialName
          ? [
              {
                value: 'prototypeDataMaterialGroup.fullName',
                text: $t('prototypes.products'),
                type: 'text',
                active: true,
              },
            ]
          : undefined
      ">
      <template #custom="{ item, header }: any">
        <div v-if="header == 'condition'" class="w-full px-1">
          <BaseSelectDropdown
            :items="conditionTypes"
            :selected-item="selectedConditionType"
            flat
            size="sm"
            :mode="mode"
            @update:selected="handleConditionTypeSelection($event, item)"
            name="prototypeConditionSelection" />
        </div>
        <div v-else class="flex items-center justify-center w-full">
          <BaseCheckbox
            v-if="question.mwDataType?.name == MWDataTypes.BOOLEAN"
            v-model="item[header]"
            :disabled="mode == 'view'"
            @update:model-value="updateItem(item)" />
          <BaseInput
            v-else
            v-model="item[header]"
            type="number"
            :disabled="mode == 'view'"
            :mode="mode"
            color="light"
            flat
            @update:model-value="updateItem(item)" />
        </div>
      </template>
    </BaseTable>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { MWDataTypes } from '@/utils/constants';
import BaseTable from '../common/BaseTable.vue';
import BaseCheckbox from '../common/BaseCheckbox.vue';
import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import BaseInput from '../common/BaseInput.vue';
import type {
  QstQuestionnairePrototype,
  QstQuestionnairePrototypeData,
  Question,
} from '@/types/Questionnaires';
import { RecordMode, type SelectDropdownItem } from '@/types/Base';
import { Conditions } from '@/utils/constants';

const prototype = defineModel<QstQuestionnairePrototype | undefined>();
defineProps<{
  mode: 'view' | 'edit';
  question: Question;
}>();

const { t: $t } = useI18n();

const prototypeDataHeaders = ref([
  {
    value: prototype.value?.qstQuestionnairePrototypeData[0].prototypeDataMaterialGroup
      ?.materialName
      ? 'prototypeDataMaterialGroup.materialName'
      : 'prototypeDataMaterialGroup.fullName',
    text: $t('prototypes.products'),
    type: 'text',
    active: true,
  },
  { value: 'condition', text: $t('prototypes.condition'), type: 'custom', active: true },
  { value: 'acceptedValue', text: $t('prototypes.acceptedValue'), type: 'custom', active: true },
  {
    value: 'recommendedValue',
    text: $t('prototypes.recommendedValue'),
    type: 'custom',
    active: true,
  },
  { value: 'perfectValue', text: $t('prototypes.perfectValue'), type: 'custom', active: true },
]);

const selectedConditionType = ref<SelectDropdownItem | null>({
  'value': Conditions.Equal,
  'name': $t('prototypes.equal'),
});

const conditionTypes = ref<SelectDropdownItem[]>([
  { 'value': Conditions.Equal, 'name': $t('prototypes.equal') },
  { 'value': Conditions.NotEqual, 'name': $t('prototypes.notEqual') },
  { 'value': Conditions.GreaterThan, 'name': $t('prototypes.greaterThan') },
  { 'value': Conditions.GreaterThanOrEqual, 'name': $t('prototypes.greaterThanOrEqual') },
  { 'value': Conditions.LessThan, 'name': $t('prototypes.lessThan') },
  { 'value': Conditions.LessThanOrEqual, 'name': $t('prototypes.lessThanOrEqual') },
  { 'value': Conditions.Contains, 'name': $t('prototypes.contains') },
  { 'value': Conditions.ContainsAll, 'name': $t('prototypes.containsAll') },
]);

function handleConditionTypeSelection(
  item: SelectDropdownItem,
  data: QstQuestionnairePrototypeData
) {
  data.condition = item.value as string;
  data.recordMode = RecordMode.Modified;
}

function updateItem(item: any) {
  item.recordMode = RecordMode.Modified;
}
</script>
